/**
 * DataTable.jsx
 * -------------
 * Reusable data table component with sorting, searching, and row selection
 */

import React, { useState, useEffect } from 'react';
import { 
  Edit2, 
  Trash2, 
  Search, 
  ChevronDown, 
  ChevronUp,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';

const DataTable = ({ 
  records, 
  fields, 
  onEdit, 
  onDelete, 
  selectedRecords,
  onSelectionChange,
  loading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredRecords, setFilteredRecords] = useState([]);
  
  const recordsPerPage = 20;

  // Filter and sort records
  useEffect(() => {
    let result = [...records];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(record => {
        return fields.some(field => {
          const value = String(record[field.name] || '').toLowerCase();
          return value.includes(query);
        });
      });
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        
        if (aVal === bVal) return 0;
        
        const comparison = aVal < bVal ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    setFilteredRecords(result);
    setCurrentPage(1); // Reset to first page when filtering changes
  }, [records, searchQuery, sortField, sortDirection, fields]);

  const handleSort = (fieldName) => {
    if (sortField === fieldName) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(fieldName);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      onSelectionChange(currentPageRecords.map(r => r.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRecord = (recordId, checked) => {
    if (checked) {
      onSelectionChange([...selectedRecords, recordId]);
    } else {
      onSelectionChange(selectedRecords.filter(id => id !== recordId));
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentPageRecords = filteredRecords.slice(startIndex, endIndex);

  const allCurrentPageSelected = 
    currentPageRecords.length > 0 && 
    currentPageRecords.every(r => selectedRecords.includes(r.id));

  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (type) {
      case 'boolean':
        return value ? '✓ Yes' : '✗ No';
      case 'date':
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return String(value);
        }
      case 'number':
        return Number(value).toLocaleString();
      default:
        return String(value);
    }
  };

  if (loading) {
    return (
      <div className="data-table-loading">
        <div className="loading-spinner"></div>
        <p>Loading records...</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      {/* Search Bar */}
      <div className="data-table-header">
        <div className="data-table-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search across all fields..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="data-table-info">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredRecords.length)} of {filteredRecords.length} records
          {selectedRecords.length > 0 && (
            <span className="selection-count">
              ({selectedRecords.length} selected)
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th className="checkbox-column">
                <input
                  type="checkbox"
                  checked={allCurrentPageSelected}
                  onChange={handleSelectAll}
                />
              </th>
              {fields.map((field, index) => (
                <th 
                  key={index} 
                  onClick={() => handleSort(field.name)}
                  className="sortable-column"
                >
                  <div className="column-header">
                    <span>{field.name}</span>
                    {sortField === field.name && (
                      sortDirection === 'asc' ? 
                        <ChevronUp size={16} /> : 
                        <ChevronDown size={16} />
                    )}
                  </div>
                </th>
              ))}
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPageRecords.length === 0 ? (
              <tr>
                <td colSpan={fields.length + 2} className="empty-state">
                  {searchQuery ? 'No records match your search' : 'No records yet'}
                </td>
              </tr>
            ) : (
              currentPageRecords.map((record) => (
                <tr key={record.id}>
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedRecords.includes(record.id)}
                      onChange={(e) => handleSelectRecord(record.id, e.target.checked)}
                    />
                  </td>
                  {fields.map((field, index) => (
                    <td key={index} className={`field-${field.type}`}>
                      {formatValue(record[field.name], field.type)}
                    </td>
                  ))}
                  <td className="actions-column">
                    <div className="action-buttons">
                      <button
                        onClick={() => onEdit(record)}
                        className="btn-action btn-edit"
                        title="Edit record"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => onDelete(record)}
                        className="btn-action btn-delete"
                        title="Delete record"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="data-table-pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="btn-pagination"
            title="First page"
          >
            <ChevronsLeft size={16} />
          </button>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn-pagination"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn-pagination"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="btn-pagination"
            title="Last page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTable;






